import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * How long a filter edit sits before it reaches the URL — and so before the
 * page refetches. Long enough to swallow a burst of keystrokes or a run of
 * clicks in an open multi-select, short enough to still feel live.
 */
export const FILTER_DEBOUNCE_MS = 400;

/**
 * Filter state persisted in the URL query string, so any dashboard view is
 * a shareable link. Empty values drop the param to keep URLs clean, and
 * writes replace the history entry so filter tweaks don't bury the back
 * button.
 *
 * Writes land immediately, so this stays internal: pages go through the
 * debounced variants below rather than refetch per click.
 */
function useUrlFilter(key: string): [string, (value: string) => void] {
  const { search } = useLocation();
  const navigate = useNavigate();

  const value = new URLSearchParams(search).get(key) ?? "";

  const setValue = useCallback(
    (next: string) => {
      // Read the live location rather than the render-time snapshot so two
      // setters firing in one event handler (e.g. a sample-data button that
      // fills in both UPC lists) compose instead of clobbering each other.
      // This relies on navigation committing synchronously, which holds
      // while no route has a loader.
      const params = new URLSearchParams(window.location.search);
      if ((params.get(key) ?? "") === next) return;
      if (next) params.set(key, next);
      else params.delete(key);
      navigate({ search: params.toString() }, { replace: true });
    },
    [navigate, key],
  );

  return [value, setValue];
}

/**
 * useUrlFilter behind a debounce: returns [draft, setDraft, value] where the
 * draft tracks every edit (drive the input or chips from it) and the URL
 * value trails behind by FILTER_DEBOUNCE_MS (drive the fetcher from it) — so
 * typing, or adding several UPC chips in a row, neither hammers the warehouse
 * nor rewrites history per edit.
 */
export function useDebouncedUrlFilter(
  key: string,
): [string, (draft: string) => void, string] {
  const [value, setValue] = useUrlFilter(key);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (draft.trim() === value) return;
    const timer = setTimeout(() => setValue(draft.trim()), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, value, setValue]);

  // Follow the URL when it changes out from under us (e.g. clicking the nav
  // link of the current page resets the query string) — but leave the draft
  // alone when it's just our own debounced write echoing back, so typing a
  // trailing space isn't eaten.
  useEffect(() => {
    setDraft((d) => (d.trim() === value ? d : value));
  }, [value]);

  return [draft, setDraft, value];
}

// Dedupe and drop empty segments so a hand-edited URL like ?brand=5,5,
// can't render phantom selections.
function parseList(raw: string): string[] {
  return raw ? [...new Set(raw.split(",").filter(Boolean))] : [];
}

/**
 * List-valued variant for the multi-selects: returns [values, setValues,
 * apiValue]. `values` follows every toggle instantly so the checkboxes feel
 * live; `apiValue` is the comma-joined list ready to pass to the API
 * (undefined when empty) and trails behind the debounce, so picking five
 * brands in an open list costs one refetch rather than five.
 * Comma-joining assumes values never contain commas — ids trivially don't,
 * and DMA names are guaranteed comma-free by the same invariant the API's
 * list params rely on (gft-insights-api lambdas/shared/checkout.ts).
 */
export function useUrlListFilter(
  key: string,
): [string[], (values: string[]) => void, string | undefined] {
  const [draft, setDraft, committed] = useDebouncedUrlFilter(key);
  const values = useMemo(() => parseList(draft), [draft]);
  const setValues = useCallback(
    (next: string[]) => setDraft(next.join(",")),
    [setDraft],
  );
  return [values, setValues, parseList(committed).join(",") || undefined];
}
